import { useCallback, useState } from "react";
import { ApiError } from "@/api/orval/api-error";
import { useCreateOnDemandIrrigationEvent } from "@/api/orval/generated/irrigation-event";
import type {
    CreateOnDemandIrrigationEvent201,
    OnDemandIrrigationRejection,
    OnDemandIrrigationRejectionDevicesItem,
} from "@/api/orval/generated/schemas";
import { syncDeviceToMasterIndex } from "@/services/device-polling-service/refresh-devices";
import { syncEventToMasterIndex } from "@/services/irrigation-scheduling/sync-master-index-events";

interface CreateZoneEventParams {
    zoneId: string;
    /** Milliseconds from now until the irrigation starts; 0 starts it immediately. */
    startTimeOffsetMs: number;
    durationMs: number;
    /** Asset IDs the grower has agreed may be switched out of manual override. */
    devicesToSetToScheduleMode: string[];
}

/** Every device the server needs acknowledged, not just the ones the caller left out. */
export type ScheduleModeDevices = OnDemandIrrigationRejectionDevicesItem[];

export type CreateZoneEventResult = {
    created: CreateOnDemandIrrigationEvent201 | null;
    /** Set when the write was refused until these devices are acknowledged. Nothing was written. */
    devicesToAcknowledge?: ScheduleModeDevices;
};

/**
 * Creates a one-shot irrigation event and takes its zone's devices off manual override in a single
 * request, so each device receives one downlink carrying both rather than closing its valve for the
 * old schedule and reopening it for the new one.
 */
export const useCreateZoneEvent = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const { mutateAsync: createOnDemandEvent } = useCreateOnDemandIrrigationEvent();

    const createZoneEvent = useCallback(
        async ({
            zoneId,
            startTimeOffsetMs,
            durationMs,
            devicesToSetToScheduleMode,
        }: CreateZoneEventParams): Promise<CreateZoneEventResult> => {
            setLoading(true);
            setError(null);
            try {
                const created = await createOnDemandEvent({
                    data: {
                        startTimeOffsetMs,
                        durationMs,
                        zonesActive: [zoneId],
                        devicesToSetToScheduleMode,
                    },
                });
                syncEventToMasterIndex(created);
                // The switched devices come back with the write, so the banner and the device panel
                // are right immediately rather than waiting on the invalidation refetch.
                created.syncedDevices.forEach(syncDeviceToMasterIndex);
                return { created };
            } catch (errorEvent: unknown) {
                const rejection =
                    errorEvent instanceof ApiError && errorEvent.status === 409
                        ? (errorEvent.data as OnDemandIrrigationRejection)
                        : undefined;
                // An acknowledgment rejection naming no devices leaves nothing to confirm, so it takes
                // the generic failure path rather than prompting on an empty list.
                if (rejection?.code === "requiresScheduleModeAcknowledgment" && rejection.devices?.length) {
                    return { created: null, devicesToAcknowledge: rejection.devices };
                }
                setError(errorEvent instanceof Error ? errorEvent : new Error(String(errorEvent)));
                return { created: null };
            } finally {
                setLoading(false);
            }
        },
        [createOnDemandEvent],
    );

    return {
        createZoneEvent,
        loading,
        error,
    };
};
